import {
  sanitizeTableCellHtml,
  tableCellHtmlToPlainText,
  tableCellTextToEditorHtml,
} from './tableCellContent';


const roundToTwo = (value) => Math.round(value * 100) / 100;

export const equalPercentageWidths = (columns) => {
  const count = Number.isInteger(columns) && columns > 0 ? columns : 1;
  const base = Math.floor((10000 / count)) / 100;
  const values = Array.from({ length: count }, () => base);
  values[count - 1] = roundToTwo(100 - base * (count - 1));
  return values;
};

const finiteNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const validateColumnLayout = (columns, layout) => {
  if (!Number.isInteger(columns) || columns < 1) {
    return {
      valid: false,
      error: 'Le tableau doit avoir au moins une colonne.',
      value: null,
    };
  }
  if (!layout || !['percent', 'px'].includes(layout.unit)) {
    return {
      valid: false,
      error: "L’unité doit être le pourcentage ou les pixels.",
      value: null,
    };
  }
  if (!Array.isArray(layout.values) || layout.values.length !== columns) {
    return {
      valid: false,
      error: 'Une largeur est requise pour chaque colonne.',
      value: null,
    };
  }

  const values = layout.values.map(finiteNumber);
  if (values.some((value) => value === null)) {
    return {
      valid: false,
      error: 'Chaque largeur doit être un nombre.',
      value: null,
    };
  }

  if (layout.unit === 'percent') {
    if (values.some((value) => value <= 0 || value > 100)) {
      return {
        valid: false,
        error: 'Chaque pourcentage doit être compris entre 0 et 100.',
        value: null,
      };
    }
    if (Math.abs(values.reduce((sum, value) => sum + value, 0) - 100) > 0.1) {
      return {
        valid: false,
        error: 'Les pourcentages doivent totaliser 100.',
        value: null,
      };
    }
    return {
      valid: true,
      error: '',
      value: {
        unit: 'percent',
        values: values.map(roundToTwo),
      },
    };
  }

  if (
    values.some(
      (value) => !Number.isInteger(value) || value < 40 || value > 2000
    )
  ) {
    return {
      valid: false,
      error: 'Chaque largeur doit être un entier entre 40 et 2000 px.',
      value: null,
    };
  }
  return {
    valid: true,
    error: '',
    value: { unit: 'px', values },
  };
};

const tableColumnCount = (table) => {
  if (Number.isInteger(table?.columns) && table.columns > 0) {
    return table.columns;
  }
  const firstRow = Array.isArray(table?.grid) ? table.grid[0] : null;
  return Array.isArray(firstRow) && firstRow.length > 0 ? firstRow.length : 1;
};

export const getColumnLayout = (table) => {
  const columns = tableColumnCount(table);
  const result = validateColumnLayout(columns, table?.column_widths);
  if (result.valid) {
    return { ...result.value, legacy: false };
  }
  return {
    unit: 'percent',
    values: equalPercentageWidths(columns),
    legacy: true,
  };
};

const rowsFromTable = (table) => {
  if (Array.isArray(table?.grid) && table.grid.length) {
    return table.grid.map((row) => (Array.isArray(row) ? [...row] : [row]));
  }
  const content = Array.isArray(table?.content)
    ? table.content
    : typeof table?.content === 'string'
      ? table.content.split(/\r?\n/)
      : [];
  return content
    .filter((row) => row !== '')
    .map((row) =>
      Array.isArray(row)
        ? [...row]
        : String(row).split('|').map((cell) => cell.trim())
    );
};

const normalizeTableShapeWithoutSpans = (table, requestedColumns) => {
  const source = table && typeof table === 'object' ? table : {};
  const previousColumns = tableColumnCount(source);
  const columns =
    Number.isInteger(requestedColumns) && requestedColumns > 0
      ? requestedColumns
      : previousColumns;
  let grid = rowsFromTable(source);
  const rowCount =
    grid.length ||
    (Number.isInteger(source.rows) && source.rows > 0 ? source.rows : 1);
  if (!grid.length) {
    grid = Array.from({ length: rowCount }, () => []);
  }
  grid = grid.map((row) =>
    Array.from({ length: columns }, (_, index) => row[index] ?? '')
  );

  const existingLayout = validateColumnLayout(
    previousColumns,
    source.column_widths
  );
  const columnWidths =
    columns === previousColumns && existingLayout.valid
      ? existingLayout.value
      : {
          unit: 'percent',
          values: equalPercentageWidths(columns),
        };

  return {
    ...source,
    rows: grid.length,
    columns,
    grid,
    content: grid.map((row) => row.join(' | ')).join('\n'),
    column_widths: columnWidths,
  };
};

export const IMAGE_LANGUAGES = Object.freeze(['fr', 'en', 'pt']);

const localizedCaptions = (captions, legacyCaption = '') =>
  Object.fromEntries(
    IMAGE_LANGUAGES.map((language) => [
      language,
      String(captions?.[language] ?? legacyCaption ?? ''),
    ])
  );

const legacySlotId = (index) => `img_legacy-${index + 1}`;
const legacyProposalId = (index) => `proposal_legacy-${index + 1}`;

export const createImageSlot = ({
  id,
  proposalId,
  url,
  captions = {},
  caption = '',
  placement = { type: 'gallery' },
  activeLanguages = IMAGE_LANGUAGES,
}) => {
  const hasProposal = Boolean(url);
  const activeSet = new Set(activeLanguages);
  return {
    id,
    placement,
    active_proposal_ids: Object.fromEntries(
      IMAGE_LANGUAGES.map((language) => [
        language,
        hasProposal && activeSet.has(language) ? proposalId : null,
      ])
    ),
    proposals: hasProposal
      ? [{ id: proposalId, url, captions: localizedCaptions(captions, caption) }]
      : [],
  };
};

export const normalizeImageSlot = (image, index = 0) => {
  if (image && typeof image === 'object' && Array.isArray(image.proposals)) {
    return {
      ...image,
      id: image.id || legacySlotId(index),
      placement: image.placement || { type: 'gallery' },
      active_proposal_ids: Object.fromEntries(
        IMAGE_LANGUAGES.map((language) => [
          language,
          image.active_proposal_ids?.[language] ?? null,
        ])
      ),
      proposals: image.proposals.map((proposal) => ({
        ...proposal,
        captions: localizedCaptions(proposal.captions, proposal.caption),
      })),
    };
  }

  const source = typeof image === 'string' ? { url: image } : (image || {});
  return createImageSlot({
    id: source.id || legacySlotId(index),
    proposalId: legacyProposalId(index),
    url: source.url || '',
    caption: source.caption || '',
    placement: source.placement || { type: 'gallery' },
  });
};

export const normalizeImageSlots = (images) =>
  Array.isArray(images) ? images.map(normalizeImageSlot) : [];

export const resolveActiveImage = (slotValue, language = 'fr') => {
  const slot = normalizeImageSlot(slotValue);
  const activeId = slot.active_proposal_ids[language];
  const proposal = slot.proposals.find(({ id }) => id === activeId);
  return proposal
    ? {
        id: slot.id,
        proposalId: proposal.id,
        url: proposal.url,
        caption: proposal.captions[language],
        placement: slot.placement,
        slot,
      }
    : null;
};

export const assignProposalLanguages = (slot, proposalId, languages) => {
  const selected = new Set(languages);
  return {
    ...slot,
    active_proposal_ids: Object.fromEntries(
      IMAGE_LANGUAGES.map((language) => [
        language,
        selected.has(language)
          ? proposalId
          : slot.active_proposal_ids[language] === proposalId
            ? null
            : slot.active_proposal_ids[language],
      ])
    ),
  };
};

export const removeImageProposal = (slot, proposalId) => ({
  ...slot,
  proposals: slot.proposals.filter(({ id }) => id !== proposalId),
  active_proposal_ids: Object.fromEntries(
    IMAGE_LANGUAGES.map((language) => [
      language,
      slot.active_proposal_ids[language] === proposalId
        ? null
        : slot.active_proposal_ids[language],
    ])
  ),
});

export const getGalleryImageSlots = (images) =>
  normalizeImageSlots(images).filter(
    (slot) => slot.placement?.type !== 'inline'
  );

export const getGalleryImages = (images, language = 'fr') =>
  getGalleryImageSlots(images)
    .map((slot) => resolveActiveImage(slot, language))
    .filter(Boolean);

const cellKey = (row, column) => `${row}:${column}`;

const normalizeCellContentsForShape = (
  cellContents,
  grid,
  rows,
  columns
) => {
  const byCoordinate = new Map();
  (Array.isArray(cellContents) ? cellContents : []).forEach((candidate) => {
    const row = Number(candidate?.row);
    const column = Number(candidate?.column);
    if (
      !Number.isInteger(row) ||
      !Number.isInteger(column) ||
      row < 0 ||
      column < 0 ||
      row >= rows ||
      column >= columns
    ) {
      return;
    }
    const html = sanitizeTableCellHtml(candidate?.html);
    const fallback = tableCellTextToEditorHtml(grid[row][column]);
    if (!html || html === fallback) {
      byCoordinate.delete(cellKey(row, column));
      return;
    }
    byCoordinate.set(cellKey(row, column), { row, column, html });
  });
  return [...byCoordinate.values()].sort(
    (left, right) => left.row - right.row || left.column - right.column
  );
};

const normalizeCellStylesForShape = (cellStyles, rows, columns) => {
  const byCoordinate = new Map();
  (Array.isArray(cellStyles) ? cellStyles : []).forEach((candidate) => {
    const row = Number(candidate?.row);
    const column = Number(candidate?.column);
    if (
      !Number.isInteger(row) ||
      !Number.isInteger(column) ||
      row < 0 ||
      column < 0 ||
      row >= rows ||
      column >= columns
    ) {
      return;
    }
    const style = { row, column };
    if (/^#[0-9a-f]{6}$/i.test(candidate?.background_color || '')) {
      style.background_color = candidate.background_color.toUpperCase();
    }
    if (candidate?.text_direction === 'vertical-up') {
      style.text_direction = 'vertical-up';
    }
    if (['left', 'center', 'right', 'justify'].includes(candidate?.text_align)) {
      style.text_align = candidate.text_align;
    }
    if (style.background_color || style.text_direction || style.text_align) {
      byCoordinate.set(cellKey(row, column), style);
    } else {
      byCoordinate.delete(cellKey(row, column));
    }
  });
  return [...byCoordinate.values()].sort(
    (left, right) => left.row - right.row || left.column - right.column
  );
};

export const normalizeCellStyles = (table) => {
  const shaped = normalizeTableShapeWithoutSpans(table);
  return normalizeCellStylesForShape(
    table?.cell_styles,
    shaped.rows,
    shaped.columns
  );
};

export const normalizeCellContents = (table) => {
  const shaped = normalizeTableShapeWithoutSpans(table);
  return normalizeCellContentsForShape(
    table?.cell_contents,
    shaped.grid,
    shaped.rows,
    shaped.columns
  );
};

const normalizeCellSpansForShape = (cellSpans, rows, columns) => {
  const occupied = new Set();
  const normalized = [];

  (Array.isArray(cellSpans) ? cellSpans : []).forEach((candidate) => {
    const span = {
      row: Number(candidate?.row),
      column: Number(candidate?.column),
      rowspan: Number(candidate?.rowspan),
      colspan: Number(candidate?.colspan),
    };
    if (
      !Object.values(span).every(Number.isInteger) ||
      span.row < 0 ||
      span.column < 0 ||
      span.rowspan < 1 ||
      span.colspan < 1 ||
      (span.rowspan === 1 && span.colspan === 1) ||
      span.row + span.rowspan > rows ||
      span.column + span.colspan > columns
    ) {
      return;
    }

    const coordinates = [];
    for (let row = span.row; row < span.row + span.rowspan; row += 1) {
      for (
        let column = span.column;
        column < span.column + span.colspan;
        column += 1
      ) {
        coordinates.push(cellKey(row, column));
      }
    }
    if (coordinates.some((key) => occupied.has(key))) return;

    coordinates.forEach((key) => occupied.add(key));
    normalized.push(span);
  });

  return normalized.sort(
    (left, right) => left.row - right.row || left.column - right.column
  );
};

export const normalizeCellSpans = (table) => {
  const shaped = normalizeTableShapeWithoutSpans(table);
  return normalizeCellSpansForShape(
    table?.cell_spans,
    shaped.rows,
    shaped.columns
  );
};

export const normalizeTableShape = (table, requestedColumns) => {
  const shaped = normalizeTableShapeWithoutSpans(table, requestedColumns);
  return {
    ...shaped,
    cell_spans: normalizeCellSpansForShape(
      table?.cell_spans,
      shaped.rows,
      shaped.columns
    ),
    cell_styles: normalizeCellStylesForShape(
      table?.cell_styles,
      shaped.rows,
      shaped.columns
    ),
    cell_contents: normalizeCellContentsForShape(
      table?.cell_contents,
      shaped.grid,
      shaped.rows,
      shaped.columns
    ),
  };
};

export const getTableCellRichHtml = (table, { row, column } = {}) =>
  normalizeCellContents(table).find(
    (content) => content.row === row && content.column === column
  )?.html || null;

export const getTableCellEditorHtml = (table, { row, column } = {}) => {
  const shaped = normalizeTableShape(table);
  const richHtml = shaped.cell_contents.find(
    (content) => content.row === row && content.column === column
  )?.html;
  if (richHtml) return richHtml;
  const value = shaped.grid?.[row]?.[column] ?? '';
  return tableCellTextToEditorHtml(value);
};

export const getTableCellStyle = (table, { row, column } = {}) =>
  normalizeCellStyles(table).find(
    (style) => style.row === row && style.column === column
  ) || null;

export const updateTableCell = (table, { row, column } = {}, value = '') => {
  const shaped = normalizeTableShape(table);
  if (
    !Number.isInteger(row) ||
    !Number.isInteger(column) ||
    row < 0 ||
    column < 0 ||
    row >= shaped.rows ||
    column >= shaped.columns
  ) {
    return shaped;
  }
  const grid = shaped.grid.map((gridRow) => [...gridRow]);
  grid[row][column] = String(value ?? '');
  return {
    ...shaped,
    grid,
    content: grid.map((gridRow) => gridRow.join(' | ')).join('\n'),
  };
};

export const updateTableCellContent = (
  table,
  { row, column } = {},
  html = ''
) => {
  const shaped = normalizeTableShape(table);
  if (
    !Number.isInteger(row) ||
    !Number.isInteger(column) ||
    row < 0 ||
    column < 0 ||
    row >= shaped.rows ||
    column >= shaped.columns
  ) {
    return shaped;
  }
  const safeHtml = sanitizeTableCellHtml(html);
  const plainText = tableCellHtmlToPlainText(safeHtml);
  const grid = shaped.grid.map((gridRow) => [...gridRow]);
  grid[row][column] = plainText;
  const cellContents = shaped.cell_contents.filter(
    (content) => content.row !== row || content.column !== column
  );
  if (safeHtml && safeHtml !== tableCellTextToEditorHtml(plainText)) {
    cellContents.push({ row, column, html: safeHtml });
  }
  cellContents.sort(
    (left, right) => left.row - right.row || left.column - right.column
  );
  return {
    ...shaped,
    grid,
    content: grid.map((gridRow) => gridRow.join(' | ')).join('\n'),
    cell_contents: cellContents,
  };
};

export const styleTableCell = (table, { row, column } = {}, patch = {}) => {
  const shaped = normalizeTableShape(table);
  if (
    !Number.isInteger(row) ||
    !Number.isInteger(column) ||
    row < 0 ||
    column < 0 ||
    row >= shaped.rows ||
    column >= shaped.columns
  ) {
    return shaped;
  }
  const current = getTableCellStyle(shaped, { row, column }) || {
    row,
    column,
  };
  const next = { ...current };
  if (Object.prototype.hasOwnProperty.call(patch, 'background_color')) {
    if (/^#[0-9a-f]{6}$/i.test(patch.background_color || '')) {
      next.background_color = patch.background_color.toUpperCase();
    } else {
      delete next.background_color;
    }
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'text_direction')) {
    if (patch.text_direction === 'vertical-up') {
      next.text_direction = 'vertical-up';
    } else {
      delete next.text_direction;
    }
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'text_align')) {
    if (['left', 'center', 'right', 'justify'].includes(patch.text_align)) {
      next.text_align = patch.text_align;
    } else {
      delete next.text_align;
    }
  }
  const cellStyles = shaped.cell_styles.filter(
    (style) => style.row !== row || style.column !== column
  );
  if (next.background_color || next.text_direction || next.text_align) {
    cellStyles.push(next);
  }
  return {
    ...shaped,
    cell_styles: cellStyles.sort(
      (left, right) => left.row - right.row || left.column - right.column
    ),
  };
};

export const getTableCellOwnership = (table) => {
  const ownership = new Map();
  normalizeCellSpans(table).forEach((span) => {
    for (let row = span.row; row < span.row + span.rowspan; row += 1) {
      for (
        let column = span.column;
        column < span.column + span.colspan;
        column += 1
      ) {
        ownership.set(cellKey(row, column), span);
      }
    }
  });
  return ownership;
};

export const mergeTableCell = (table, { row, column, direction } = {}) => {
  const source = table && typeof table === 'object' ? table : {};
  const shaped = normalizeTableShape(source);
  if (
    !Number.isInteger(row) ||
    !Number.isInteger(column) ||
    row < 0 ||
    column < 0 ||
    row >= shaped.rows ||
    column >= shaped.columns ||
    !['right', 'down'].includes(direction)
  ) {
    return {
      table: source,
      error: 'La fusion dépasserait le tableau.',
    };
  }

  const ownership = getTableCellOwnership(shaped);
  const owner = ownership.get(cellKey(row, column)) || {
    row,
    column,
    rowspan: 1,
    colspan: 1,
  };
  const next = { ...owner };
  const addedCoordinates = [];

  if (direction === 'right') {
    const addedColumn = owner.column + owner.colspan;
    if (addedColumn >= shaped.columns) {
      return {
        table: source,
        error: 'La fusion dépasserait le tableau.',
      };
    }
    for (
      let addedRow = owner.row;
      addedRow < owner.row + owner.rowspan;
      addedRow += 1
    ) {
      addedCoordinates.push(cellKey(addedRow, addedColumn));
    }
    next.colspan += 1;
  } else {
    const addedRow = owner.row + owner.rowspan;
    if (addedRow >= shaped.rows) {
      return {
        table: source,
        error: 'La fusion dépasserait le tableau.',
      };
    }
    for (
      let addedColumn = owner.column;
      addedColumn < owner.column + owner.colspan;
      addedColumn += 1
    ) {
      addedCoordinates.push(cellKey(addedRow, addedColumn));
    }
    next.rowspan += 1;
  }

  if (addedCoordinates.some((key) => ownership.has(key))) {
    return {
      table: source,
      error: 'La fusion chevaucherait une autre cellule fusionnée.',
    };
  }

  const nextSpans = shaped.cell_spans.filter(
    (span) => span.row !== owner.row || span.column !== owner.column
  );
  nextSpans.push(next);
  nextSpans.sort(
    (left, right) => left.row - right.row || left.column - right.column
  );

  return {
    table: {
      ...shaped,
      cell_spans: nextSpans,
      grid: shaped.grid.map((gridRow) => [...gridRow]),
    },
    error: '',
  };
};

export const mergeTableCellRange = (
  table,
  { startRow, startColumn, endRow, endColumn } = {}
) => {
  const source = table && typeof table === 'object' ? table : {};
  const shaped = normalizeTableShape(source);
  const coordinates = [startRow, startColumn, endRow, endColumn];
  if (
    !coordinates.every(Number.isInteger) ||
    Math.min(startRow, endRow) < 0 ||
    Math.min(startColumn, endColumn) < 0 ||
    Math.max(startRow, endRow) >= shaped.rows ||
    Math.max(startColumn, endColumn) >= shaped.columns
  ) {
    return { table: source, error: 'La sélection dépasse les limites du tableau.' };
  }

  const top = Math.min(startRow, endRow);
  const bottom = Math.max(startRow, endRow);
  const left = Math.min(startColumn, endColumn);
  const right = Math.max(startColumn, endColumn);
  if (top === bottom && left === right) {
    return { table: source, error: 'Sélectionnez au moins deux cellules.' };
  }

  const ownership = getTableCellOwnership(shaped);
  for (let row = top; row <= bottom; row += 1) {
    for (let column = left; column <= right; column += 1) {
      const owner = ownership.get(cellKey(row, column));
      if (
        owner &&
        (owner.row < top ||
          owner.column < left ||
          owner.row + owner.rowspan - 1 > bottom ||
          owner.column + owner.colspan - 1 > right)
      ) {
        return {
          table: source,
          error: 'La sélection chevaucherait une cellule déjà fusionnée.',
        };
      }
    }
  }

  const nextSpans = shaped.cell_spans.filter(
    (span) =>
      span.row < top ||
      span.row > bottom ||
      span.column < left ||
      span.column > right
  );
  nextSpans.push({
    row: top,
    column: left,
    rowspan: bottom - top + 1,
    colspan: right - left + 1,
  });
  nextSpans.sort(
    (leftSpan, rightSpan) =>
      leftSpan.row - rightSpan.row || leftSpan.column - rightSpan.column
  );
  return {
    table: {
      ...shaped,
      cell_spans: nextSpans,
      grid: shaped.grid.map((gridRow) => [...gridRow]),
      cell_styles: shaped.cell_styles.map((style) => ({ ...style })),
    },
    error: '',
  };
};

export const unmergeTableCell = (table, { row, column } = {}) => {
  const shaped = normalizeTableShape(table);
  const owner = getTableCellOwnership(shaped).get(cellKey(row, column));
  if (!owner) return shaped;

  return {
    ...shaped,
    cell_spans: shaped.cell_spans.filter(
      (span) => span.row !== owner.row || span.column !== owner.column
    ),
    grid: shaped.grid.map((gridRow) => [...gridRow]),
  };
};

export const createInlineImage = ({
  id,
  url,
  caption = '',
  align = 'center',
  widthUnit = 'percent',
  widthValue = 60,
  wrap = 'none',
}) =>
  createImageSlot({
    id,
    proposalId: `proposal_${String(id).replace(/^img_/, '')}`,
    url,
    caption,
    placement: {
      type: 'inline',
      align,
      width: {
        unit: widthUnit,
        value: finiteNumber(widthValue),
      },
      wrap,
    },
  });

export const findInlineImage = (images, imageId, language = 'fr') => {
  const slot = normalizeImageSlots(images).find(
    (imageSlot) =>
      imageSlot.id === imageId && imageSlot.placement?.type === 'inline'
  );
  return slot ? resolveActiveImage(slot, language) || undefined : undefined;
};
